import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { 
  Database, 
  FileText, 
  Settings, 
  RefreshCw, 
  Info,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { apiModeManager, type ApiMode } from '@/lib/api-mode-manager'
import { useAuth } from '@/hooks/use-auth'

export function ApiModeToggle() {
  const { user } = useAuth()
  const [isVisible, setIsVisible] = useState(false)
  const [currentMode, setCurrentMode] = useState<ApiMode>(apiModeManager.getCurrentMode())
  const [isLoading, setIsLoading] = useState(false)
  const [apiInfo, setApiInfo] = useState(apiModeManager.getApiInfo())

  // 開發工具：正式 build 一律隱藏；開發環境限 admin
  if (import.meta.env.PROD) {
    return null
  }
  // 只有 admin 角色才能看到此組件
  if (!user || user.role !== 'admin') {
    return null
  }

  useEffect(() => {
    // 監聽模式變更
    const unsubscribe = apiModeManager.addListener((newMode) => {
      setCurrentMode(newMode)
      setApiInfo(apiModeManager.getApiInfo())
    })

    return unsubscribe
  }, [])

  const handleModeSwitch = async (newMode: ApiMode) => {
    setIsLoading(true)
    try {
      apiModeManager.setMode(newMode)
      
      // 給用戶一些視覺反饋
      await new Promise(resolve => setTimeout(resolve, 300))
      
      console.log(`Switched to ${newMode.toUpperCase()} mode`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (confirm('確定要重置為環境預設設置嗎？')) {
      apiModeManager.resetToDefault()
    }
  }

  const getModeIcon = (mode: ApiMode) => {
    return mode === 'mock' ? <FileText className="h-4 w-4" /> : <Database className="h-4 w-4" />
  }

  const getModeColor = (mode: ApiMode) => {
    return mode === 'mock' ? 'bg-blue-600/10 text-blue-600' : 'bg-success/10 text-success'
  }

  const getStatusIcon = () => {
    if (apiInfo.isOverride) {
      return <AlertCircle className="h-4 w-4 text-orange-600" />
    }
    return <CheckCircle className="h-4 w-4 text-success" />
  }

  return (
    <div className="fixed top-4 right-4 z-50">
      {!isVisible ? (
        <Button
          onClick={() => setIsVisible(true)}
          className="rounded-full h-12 w-12"
          variant="outline"
          title="API 模式切換"
        >
          <Settings className="h-5 w-5" />
        </Button>
      ) : (
        <Card className="w-80">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                API 模式控制
              </div>
              <Button
                onClick={() => setIsVisible(false)}
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
              >
                ×
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* 當前狀態 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">當前模式</span>
                <div className="flex items-center gap-2">
                  {getStatusIcon()}
                  <Badge className={getModeColor(currentMode)}>
                    <div className="flex items-center gap-1">
                      {getModeIcon(currentMode)}
                      {currentMode === 'mock' ? 'Mock API' : 'Database'}
                    </div>
                  </Badge>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">API URL</span>
                <span className="text-xs text-muted-foreground font-mono">
                  {currentMode === 'mock' ? 'localStorage' : apiInfo.apiUrl}
                </span>
              </div>
            </div>

            {/* 模式切換按鈕 */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-muted-foreground">切換模式:</h4>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={() => handleModeSwitch('mock')}
                  disabled={isLoading || currentMode === 'mock'}
                  variant={currentMode === 'mock' ? 'default' : 'outline'}
                  className="flex items-center gap-2 text-xs"
                >
                  {isLoading && currentMode !== 'mock' ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : (
                    <FileText className="h-3 w-3" />
                  )}
                  Mock API
                </Button>
                
                <Button
                  onClick={() => handleModeSwitch('database')}
                  disabled={isLoading || currentMode === 'database'}
                  variant={currentMode === 'database' ? 'default' : 'outline'}
                  className="flex items-center gap-2 text-xs"
                >
                  {isLoading && currentMode !== 'database' ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : (
                    <Database className="h-3 w-3" />
                  )}
                  Database
                </Button>
              </div>
            </div>

            {/* 環境信息 */}
            <div className="space-y-2 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Info className="h-3 w-3 text-blue-600" />
                <span className="text-xs font-medium text-muted-foreground">環境信息</span>
              </div>
              
              <div className="text-xs text-muted-foreground space-y-1">
                <div className="flex justify-between">
                  <span>環境預設:</span>
                  <span className="font-mono">{apiInfo.defaultMode}</span>
                </div>
                <div className="flex justify-between">
                  <span>VITE_USE_MOCK:</span>
                  <span className="font-mono">{apiInfo.envUseMock.toString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>用戶覆寫:</span>
                  <span className="font-mono">{apiInfo.isOverride ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </div>

            {/* 重置按鈕 */}
            {apiInfo.isOverride && (
              <Button
                onClick={handleReset}
                variant="secondary"
                size="sm"
                className="w-full text-xs"
              >
                重置為環境預設
              </Button>
            )}

            {/* 說明 */}
            <div className="text-xs text-muted-foreground pt-2 border-t">
              <div className="space-y-1">
                <div><strong>Mock API:</strong> 使用本地 localStorage 存儲</div>
                <div><strong>Database:</strong> 連接真實後端 API</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}